import { useContext, useEffect, useState, ReactNode, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PencilIcon } from '@heroicons/react/outline'

import { classNames } from 'utils'
import { AuthContext } from 'modules/Auth'
import { Role } from 'models'

import { ChipTab } from './Chips'
import { OrderTab } from './Orders'
import { DeviceTab } from './Devices'
import { OverallTab } from './Overall'
import { SubscriptionTab } from './Subscription'
import { InvoiceTab } from './Invoices'

import { ClientTab } from './Client'
import { StatusTab } from './Status'
import { AddressesTab } from './Addresses'
import { EnterpriseTab } from './Enterprise'
import { UsersTab } from './Users'
import { StaffTab } from './Staff'
import { BillingTab as EditBillingTab } from './Billing'
import { AddUserButton } from './Users/components/AddUserButton'
import { AddAddressButton } from './Addresses/components/AddAddressButton'
import { SimulateCancellationButton } from './Chips/components/SimulateCancellationButton'

type Tab = {
  id: string
  title: string
  subtitle?: string
  headerAction?: ReactNode
  current: boolean
  component: ReactNode
  editComponent?: ReactNode
  editRoles?: Role[]
  editLabel?: string
  roles?: Role[]
}

const defaultTabs: Tab[] = [
  {
    id: 'general',
    title: 'Geral',
    subtitle: 'Dados cadastrais e informações do cliente',
    current: false,
    component: <OverallTab />,
    editComponent: <ClientTab />,
    editLabel: 'Editar'
  },
  {
    id: 'status',
    title: 'Status',
    subtitle: 'Situação do cliente e histórico de mudanças',
    current: false,
    component: <StatusTab />,
    roles: [Role.ROOT, Role.MANAGER, Role.FINANCE]
  },
  {
    id: 'invoices',
    title: 'Faturas',
    subtitle: 'Cobranças geradas e histórico de pagamentos',
    current: false,
    component: <InvoiceTab />,
    editComponent: <EditBillingTab />,
    editRoles: [Role.ROOT, Role.MANAGER, Role.FINANCE],
    editLabel: 'Opções de faturamento'
  },
  {
    id: 'subscription',
    title: 'Assinaturas',
    subtitle: 'Planos ativos e serviços contratados',
    current: false,
    component: <SubscriptionTab />
  },
  {
    id: 'orders',
    title: 'Pedidos',
    subtitle: 'Solicitações de compra e status de entrega',
    current: false,
    component: <OrderTab />
  },
  {
    id: 'devices',
    title: 'Rastreadores',
    subtitle: 'Dispositivos vinculados à frota do cliente',
    current: false,
    component: <DeviceTab />
  },
  {
    id: 'chips',
    title: 'Chips',
    subtitle: 'SIM cards associados aos rastreadores',
    headerAction: <SimulateCancellationButton />,
    current: false,
    component: <ChipTab />
  },
  {
    id: 'address',
    title: 'Endereços',
    subtitle: 'Localidades cadastradas para este cliente',
    headerAction: <AddAddressButton />,
    current: false,
    component: <AddressesTab />
  },
  {
    id: 'enterprise',
    title: 'Empresa',
    subtitle: 'Dados da empresa e contas vinculadas',
    current: false,
    component: <EnterpriseTab />
  },
  {
    id: 'user',
    title: 'Administradores',
    subtitle: 'Usuários com acesso à plataforma',
    headerAction: <AddUserButton />,
    current: false,
    component: <UsersTab />
  },
  {
    id: 'staff',
    title: 'Staff',
    subtitle: 'Membros de equipe com acesso a este cliente',
    current: false,
    component: <StaffTab />,
    roles: [
      Role.ROOT,
      Role.MANAGER,
      Role.SALES,
      Role.FINANCE,
      Role.SUPPORT,
      Role.LOGISTICS
    ]
  }
]

export const ViewTabs = () => {
  const navigate = useNavigate()

  const { clientId, clientTab } = useParams() as unknown as {
    clientId: number
    clientTab: string
  }

  const { user } = useContext(AuthContext)
  const userRole = user?.relationships?.role.attributes.name as Role | undefined
  const allowedTabs = defaultTabs.filter(
    (t) => !t.roles || (userRole && t.roles.includes(userRole))
  )

  const [tabs, setTabs] = useState<Tab[]>(allowedTabs)
  const [active, setActive] = useState<Tab>()
  const [editingTab, setEditingTab] = useState<string | null>(null)

  useEffect(() => {
    if (!clientId || !clientTab) return

    const index = allowedTabs.findIndex((t) => t.id === clientTab)
    if (index === -1) return

    const updated = allowedTabs.map((t, i) => ({ ...t, current: i === index }))

    setTabs(updated)
    setActive(updated[index])
  }, [clientId, clientTab])

  const onTabClick = useCallback(
    (selected: Tab) => {
      if (!clientId) return

      selected.current = true

      setActive(selected)
      setEditingTab(null)

      navigate(`/clients/${clientId}/${selected.id}`, { replace: true })
    },
    [clientId, navigate]
  )

  const canEdit =
    userRole !== undefined &&
    active?.editComponent !== undefined &&
    (!active.editRoles || active.editRoles.includes(userRole))

  return (
    <>
      <div>
        <div className="hidden sm:block">
          <div>
            <nav
              className="flex flex-row border-b border-gray-200"
              aria-label="Tabs"
            >
              {tabs?.map((tab) => (
                <button
                  key={tab.title}
                  className={classNames(
                    tab.current
                      ? 'border-emerald-500 text-emerald-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                    'whitespace-nowrap justify-between flex-1 py-3 px-1 border-b-2 font-medium text-sm bg-white'
                  )}
                  aria-current={tab.current ? 'page' : undefined}
                  onClick={() => onTabClick(tab)}
                >
                  <div className="flex flex-row justify-center items-center space-x-1">
                    <div>{tab.title}</div>
                  </div>
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {active && (
        <>
          <div className="flex items-center justify-between px-4 mt-6 mb-6">
            <div>
              <h3 className="text-xl leading-6 font-medium text-gray-900">
                {active.title}
              </h3>
              {active.subtitle && (
                <p className="mt-0.5 text-sm text-gray-500">
                  {active.subtitle}
                </p>
              )}
            </div>
            {active.headerAction ??
              (canEdit && (
                <button
                  className={classNames(
                    editingTab === active.id
                      ? 'border-emerald-500 text-emerald-600 hover:bg-emerald-50'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50',
                    'inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-md text-sm font-medium bg-white'
                  )}
                  onClick={() =>
                    setEditingTab(editingTab === active.id ? null : active.id)
                  }
                >
                  <PencilIcon className="h-3.5 w-3.5" />
                  {active.editLabel}
                </button>
              ))}
          </div>
          {editingTab === active.id && active.editComponent
            ? active.editComponent
            : active.component}
        </>
      )}
    </>
  )
}
